import React from "react";
import {
  Container,
  Card,
  CardBody,
  Button,
  Row,
  Col,
  Badge,
  Label,
  Input,
} from "reactstrap";
import { DashboardPageHeader } from "../../../Components/Common/DashboardKit";
import { useNavigate } from "react-router-dom";

/**
 * DmsDocumentLayout — wrapper for ALL DMS document create/edit pages
 * (ECN / SOP / WI / FCD / LIT all import this).
 *
 * Visual upgrade:
 *   - Polished meta header (back button + doc number + revision + status)
 *     now uses a soft tone palette matching the rest of the kit
 *   - Document Title section gets a stronger label hierarchy
 *   - Action buttons are right-aligned with icon prefixes (Save Draft +
 *     Submit For Approval) matching the canonical toolbar pattern
 *   - `disabled` prop is now WIRED — previously it was passed by callers
 *     (e.g. CreateEcn passes `disabled={!docMeta.docNumber || submitLoading}`)
 *     but the layout ignored it. Now both action buttons honor it.
 *
 * Prop interface is UNCHANGED. Every existing caller (CreateEcn,
 * CreateSop, CreateWi, CreateFcd, CreateLit — all currently point at
 * CreateEcn.js) works without modification.
 */
const DmsDocumentLayout = ({
  title,
  docNumber,
  revision,
  status,
  statusCode,
  docTitle,
  docType,
  onDocTitleChange,
  children,
  onSaveDraft,
  onSubmit,
  // Round DMS-2 (owner request #7) — legacy ribbon parity handlers.
  onCloseWithoutSaving,
  onSaveAndClose,
  // Round DMS-5 — correction-mode legacy trio.
  onSaveCorrectionAndClose,
  onSendCorrectionForApproval,
  disabled,
  // Round DMS-UAT-4 (2026) — correction mode. When Document Control edits
  // a document that is PAST the draft/pending stage (awaiting release,
  // released, etc.), the footer shows a single "Save Changes" button that
  // saves the correction in place. Save Draft / Submit For Approval are
  // hidden — an already-approved document must never be demoted to a
  // draft or re-submitted for approval by an edit (the UAT bug: editing
  // ECN-19777 at status 55 offered "Submit For Approval").
  correctionMode = false,
  onSaveCorrection,
}) => {
  const navigate = useNavigate();

  // Pick a soft tone for the status badge. Numeric status codes (edit
  // mode passes the DB code alongside the label) map to lifecycle tones;
  // otherwise fall back to the legacy text matching.
  const codeTone = {
    0: "warning",   // Pending Approval
    1: "info",      // Approved - Awaiting Release
    2: "success",   // Approved & Released
    50: "danger",   // Rejected / Needs Revision
    51: "dark",     // Obsolete
    54: "danger",   // Void
    55: "info",     // Awaiting Release (Tasks Open)
    56: "info",     // Awaiting Release (Tasks Complete)
    60: "secondary" // Draft
  }[Number(statusCode)];
  const statusTone =
    codeTone ??
    (status === "Approved"
      ? "success"
      : status === "Rejected"
        ? "danger"
        : status === "Submitted" || status === "Pending"
          ? "warning"
          : "secondary");

  return (
    <div className="page-content">
      <Container fluid>
        <DashboardPageHeader
          icon="ri-edit-box-line"
          eyebrow="DMS"
          title={title}
          subtitle={
            docNumber
              ? `Editing ${docNumber} · Revision ${revision ?? 0}`
              : `Creating new ${docType?.toUpperCase() || "document"}`
          }
          tone="blue"
          breadcrumb={[
            { label: "Dashboards", to: "/" },
            { label: "DMS", to: "/workflows?wf=dms" },
            { label: title },
          ]}
        />

        <Card className="shadow-sm border-0">
          <CardBody>
            {/* ── Document Meta Header ─────────────────── */}
            <Row className="mb-4 align-items-center g-3">
              <Col md={6} className="d-flex align-items-center gap-3">
                <Button
                  color="light"
                  className="btn-icon"
                  style={{
                    width: 38,
                    height: 38,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 8,
                  }}
                  onClick={() => navigate("/document-management")}
                  title="Back to Document Management"
                  aria-label="Back to Document Management"
                >
                  <i
                    className="ri-arrow-left-line"
                    style={{ fontSize: 18 }}
                  ></i>
                </Button>

                <div>
                  <div
                    className="text-uppercase fw-semibold text-muted"
                    style={{ fontSize: 11, letterSpacing: "0.04em" }}
                  >
                    Document Number
                  </div>
                  <h5 className="mb-0 fw-semibold">
                    {docNumber || `New ${docType?.toUpperCase() || "Document"}`}
                  </h5>
                </div>
              </Col>
              <Col md={3}>
                <div
                  className="text-uppercase fw-semibold text-muted"
                  style={{ fontSize: 11, letterSpacing: "0.04em" }}
                >
                  Revision
                </div>
                <div className="fw-semibold" style={{ fontSize: 15 }}>
                  {revision ?? 0}
                </div>
              </Col>
              <Col md={3} className="text-md-end">
                <div
                  className="text-uppercase fw-semibold text-muted mb-1"
                  style={{ fontSize: 11, letterSpacing: "0.04em" }}
                >
                  Status
                </div>
                <Badge
                  color={statusTone}
                  pill
                  className="px-3 py-2"
                  style={{ fontSize: 12 }}
                >
                  {status || "Draft"}
                </Badge>
              </Col>
            </Row>

            {/* ── Document Title ──────────────────────── */}
            <Row className="mb-4 align-items-center g-3">
              <Col md={8}>
                <Label className="fw-semibold mb-1" style={{ fontSize: 13 }}>
                  Document Title
                </Label>
                <Input
                  value={docTitle || ""}
                  onChange={(e) => onDocTitleChange(e.target.value)}
                  placeholder="Enter a descriptive title for this document"
                />
              </Col>
            </Row>

            {/* ── Form fields (provided by caller) ─────── */}
            {children}

            {/* ── Actions ─────────────────────────────── */}
            {correctionMode ? (
              <>
                {/* Round DMS-UAT-4 — Document Control correction: save in
                    place. The document keeps its status; the approval
                    record is preserved; no approval emails are re-sent;
                    the edit is written to the revision-history audit. */}
                <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top flex-wrap gap-2">
                  <div className="text-muted small">
                    <i className="ri-shield-check-line align-bottom me-1"></i>
                    Document Control correction — saving keeps the document at
                    its current status and records the edit in Revision History.
                    No re-approval is triggered.
                  </div>
                  <div className="d-flex gap-2 flex-wrap">
                    {/* Round DMS-5 — the legacy ribbon trio exists in EVERY
                        edit, corrections included. Save Changes (in place)
                        stays as the modern extra. */}
                    <Button color="light" outline onClick={onCloseWithoutSaving} disabled={disabled}>
                      <i className="ri-close-circle-line align-bottom me-1" />
                      Close Without Saving
                    </Button>
                    <Button color="success" onClick={onSaveCorrection} disabled={disabled}>
                      <i className="ri-save-3-line align-bottom me-1" />
                      Save Changes
                    </Button>
                    <Button color="secondary" onClick={onSaveCorrectionAndClose} disabled={disabled}>
                      <i className="ri-save-2-line align-bottom me-1" />
                      Save &amp; Close
                    </Button>
                    <Button color="primary" onClick={onSendCorrectionForApproval} disabled={disabled}>
                      <i className="ri-send-plane-line align-bottom me-1" />
                      Save &amp; Send For Approval
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              /* Round DMS-2 (owner request #7) — the legacy DMSNew ribbon
                 trio, in the same order and with the same semantics:
                   • Close Without Saving  → confirm, discard, leave
                   • Save & Close          → save draft, then leave
                   • Save & Send For Approval → the existing submit
                 "Save Draft" (stay on page) is KEPT as an extra modern
                 convenience — nothing removed. */
              <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top flex-wrap">
                <Button color="light" outline onClick={onCloseWithoutSaving} disabled={disabled}>
                  <i className="ri-close-circle-line align-bottom me-1" />
                  Close Without Saving
                </Button>
                <Button color="light" onClick={onSaveDraft} disabled={disabled}>
                  <i className="ri-save-3-line align-bottom me-1" />
                  Save Draft
                </Button>
                <Button color="secondary" onClick={onSaveAndClose} disabled={disabled}>
                  <i className="ri-save-2-line align-bottom me-1" />
                  Save &amp; Close
                </Button>
                <Button color="primary" onClick={onSubmit} disabled={disabled}>
                  <i className="ri-send-plane-line align-bottom me-1" />
                  Save &amp; Send For Approval
                </Button>
              </div>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default DmsDocumentLayout;
